import React, { useEffect } from 'react'
import { StandardOpenModalButton } from 'src/components/ModalForms/ModalButtons'
import {
  useHideModal,
  useIsShowModal,
  useSetActiveModal,
} from 'src/Modal/ModalStateContext'
import { ModalForm, ModalFormId } from '../../..'
import { useFrameworkWizard } from '../../../../../context/FormContext'
import { ConfirmFrameworkModal } from '../ConfirmFrameworkModal'
import { NoRecommendedTemplates } from '../NoRecommendedTemplates'
import { MultiSelectForm } from '../../../../Reusable/Forms/MultiSelectForm'
import { useDepartmentForComplianceFramework } from '../../../../async/complianceframework/useDepartmentForComplianceFramework'
import { DepartmentChipWithDepartment } from '../../../../../pages/Controls/DepartmentChip'
import { BasicChip } from '../../../../Reusable/Chips/BasicChip/BasicChip'
import {
  SelectPoliciesRowItem,
  SelectPoliciesRowItemProps,
} from './SelectPoliciesRowItem'

interface SelectPoliciesForComplianceFrameworkProps
  extends SelectPoliciesRowItemProps {
  complianceFrameworkId: string
}

const SelectPoliciesForComplianceFramework = ({
  policy,
  onSelect,
  selectedPolicyIds,
  complianceFrameworkId,
}: SelectPoliciesForComplianceFrameworkProps) => {
  const inlineDeptShortName = policy.getDepartmentShortName()
  const departmentId = policy.getDepartmentId()
  // Fall back to the per-row dept RPC only when the policy proto doesn't
  // carry an inline short_name and there's an XID to look up
  const shouldFetch = !inlineDeptShortName && departmentId !== ''
  const department = useDepartmentForComplianceFramework(
    departmentId,
    complianceFrameworkId,
    shouldFetch,
  )

  let departmentChip: JSX.Element
  if (inlineDeptShortName) {
    departmentChip = <BasicChip>{inlineDeptShortName}</BasicChip>
  } else if (shouldFetch) {
    departmentChip = <DepartmentChipWithDepartment department={department} />
  } else {
    departmentChip = <BasicChip>-</BasicChip>
  }

  return (
    <SelectPoliciesRowItem
      {...{ policy, onSelect, selectedPolicyIds }}
      customDepartmentElement={departmentChip}
    />
  )
}

export const SelectPoliciesModal = (): JSX.Element => {
  const {
    policiesState: {
      suggestedPolicies,
      selectedPolicies,
      setSelectedPolicies,
      policiesInAccount,
    },
    frameworkState: { framework },
    loadingState: { loading },
  } = useFrameworkWizard()

  const hide = useHideModal({ modalId: ModalFormId.SELECT_POLICIES })
  const show = useIsShowModal(ModalFormId.SELECT_POLICIES)
  const back = useSetActiveModal(
    policiesInAccount.length
      ? ModalFormId.REUSABLE_POLICIES
      : ModalFormId.SELECT_CONTROLS,
  )

  useEffect(() => {
    setSelectedPolicies(suggestedPolicies.map((elem) => elem.getModelId()))
  }, [setSelectedPolicies, suggestedPolicies])

  const handleDeselectAll = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    setSelectedPolicies([])
  }
  const handleSelectAll = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    setSelectedPolicies(suggestedPolicies.map((elem) => elem.getModelId()))
  }

  const onSelect = (policyIds: string[]) => {
    setSelectedPolicies(policyIds)
  }

  const multiSelectFormProps = {
    formId: ModalFormId.SELECT_POLICIES,
    title:
      'We recommend adding all of these policies. Select which ones you want.',
    loading,
    handleSelectAll,
    handleDeselectAll,
    gridItems: suggestedPolicies.length ? (
      <>
        {suggestedPolicies.map((policy) => (
          <SelectPoliciesForComplianceFramework
            key={policy.getId()}
            policy={policy}
            onSelect={onSelect}
            selectedPolicyIds={selectedPolicies}
            complianceFrameworkId={framework?.getModelId() || ''}
          />
        ))}
      </>
    ) : (
      <NoRecommendedTemplates templateType={'policies'} />
    ),
  }

  return (
    <div>
      <ConfirmFrameworkModal />
      <ModalForm
        show={show}
        hide={hide}
        formId={ModalFormId.SELECT_POLICIES}
        size="xl"
        title={`Add Policies for ${framework?.getName() || ''}`}
        onBack={back}
        customSubmitButton={
          <StandardOpenModalButton modalId={ModalFormId.CONFIRM_FRAMEWORK} />
        }
      >
        <MultiSelectForm showSelectButtons={true} {...multiSelectFormProps} />
      </ModalForm>
    </div>
  )
}
